import { Component, Inject } from "@angular/core";
import { CssStyle } from "@nms-angular-toolkit/nms-custom-text";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { ColumnConfigType, NmsTextLabelIcon, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import {
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepModel,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { PRIMARY_DANGER_COLOR, PRIMARY_PRESSED_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { CompareFlatObjectPipe } from "@nms-ng2/app/shared/pipes/compare-flat-object.pipe";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { GeneralStepModel } from "../7-general/general-step.model";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { LinksStepModel } from "../4-links/links-step.model";
import {
    Amplifier,
    AmplifierConfigInfo,
    AmplifiersStepModel,
    DwdmAmplifierData,
    DwdmAmplifierRequestData,
    DwdmCircuitAmplifierProfileData,
    LosAction,
    MODE_LABEL_MAP,
    PROFILE_LABEL_MAP
} from "./amplifiers-step.model";
import { ConfigAmplifierComponent } from "./config-amplifier/config-amplifier.component";
import { AmplifiersUtilsService } from "./services/amplifiers-utils.service";
import { AmplifiersService } from "./services/amplifiers.service";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Step Amplifiers do DwdmCircuitWizard.
 */
@Component({
    selector: "amplifiers-step",
    templateUrl: "./amplifiers-step.component.html",
    styleUrls: ["./amplifiers-step.component.scss"]
})
export class AmplifiersStepComponent extends AbstractNmsWizardStep {
    model: AmplifiersStepModel;
    endsModel: EndsStepModel;
    transpondersModel: TranspondersStepModel;
    intermediatesModel: IntermediatesStepModel;
    linksModel: LinksStepModel;
    oscsModel: OscManagementStepModel;
    generalModel: GeneralStepModel;

    originalAmplifiers: Amplifier[] = [];
    tableData: Amplifier[] = [];
    amplifiersData: DwdmAmplifierData[] = [];
    amplifiersTableColumns: SimpleTableColumnConfig[] = [];

    private identifiersInUse: Array<string> = [];
    private powerChangeAlertShown: boolean = false;

    columnsSortable: Record<string, boolean> = {
        identifier: true,
        hostname: true,
        portName: true,
        profileLabel: true,
        modeLabel: true,
        value: true,
        limit: true,
        losAction: true,
        losThreshold: true,
        actions:false
    };

    circuitDiagram: CircuitDiagram;
    isEdit: boolean = false;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(ModalFactory) private readonly modalFactory: ModalFactory,
            private readonly nmsDialogService: NmsDialogService, private readonly amplifiersService: AmplifiersService,
            private readonly amplifiersUtilsService: AmplifiersUtilsService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly compareFlatObjectPipe: CompareFlatObjectPipe,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
        this.model = new AmplifiersStepModel();
        this.initializeTableColumns();
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.identifiersInUse = [];
        this.model = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel || new AmplifiersStepModel();

        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        if (this.endsModel.circuitId) {
            this.isEdit = true;
            this.checkTransponderPowerChanges();
        }

        const aEndDevice = this.endsModel.aEndDevice.id;
        const zEndDevice = this.endsModel.zEndDevice.id;
        const linksId = this.linksModel.links.map(link => link.id);
        const dwdmAmplifierRequest: DwdmAmplifierRequestData = {
            deviceAEnd: aEndDevice,
            deviceZEnd: zEndDevice,
            linkIds: linksId
        };

        this.amplifiersData = await this.amplifiersService.getAmplifiers(dwdmAmplifierRequest).toPromise();
        this.originalAmplifiers = this.amplifiersUtilsService
            .convertAmplifiersToOriginals(this.amplifiersData, this.identifiersInUse);
        let configuredAmplifiers = this.model.configuredAmplifiers;

        if (this.model.circuitId && this.model.configuredAmplifiers?.length) {
            this.identifiersInUse = [];

            configuredAmplifiers = this.amplifiersUtilsService.loadAmplifiers(this.model.configuredAmplifiers,
                this.identifiersInUse);
        }

        this.setTableData(configuredAmplifiers);
        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        this.resolveModel();
        const amplifiersValidatorData = this.circuitWizardValidationUtilsService.buildAmplifiersData(this.model);

        return this.validate().then(() => this.circuitValidationService.validateAmplifiers(amplifiersValidatorData));
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        const isSomeAmplifierNotConfigured = this.tableData.some((amplifier: Amplifier) => !amplifier.value);

        if (isSomeAmplifierNotConfigured) {
            const errorMessage = this.translate.instant("dwdm.circuit.wizard.amplifiers.not.configured.error");
            return Promise.reject(new NmsWizardStepValidationResponse(false, errorMessage));
        }

        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        this.model.configuredAmplifiers = this.tableData;

        return Promise.resolve(this.model);
    }

    cleanAmplifierFromTable(amplifier: Amplifier) {
        if (this.amplifiersUtilsService.isAmplifierConfigured(amplifier.value, amplifier.limit, amplifier.losThreshold)) {
            this.nmsDialogService.openDialog({
                title: this.translate.instant("popups.confirm.confirm"),
                description: this.translate.instant("dwdm.circuit.wizard.table.amplifiers.reset.amplifiers"),
                isConfirm: true
            }).then(() => {
               const originalItem = this.originalAmplifiers.find(original => this.isSameAmplifier(original, amplifier));

                if (!originalItem) {
                    return;
                }

                this.tableData = this.tableData.map(amplifierTable => {
                    if (this.isSameAmplifier(amplifierTable, amplifier)) {
                        return { ...originalItem, identifier: amplifier.identifier };
                    }

                    return amplifierTable;
                });
                this.emitChangesWereMade(this.getCurrentStepModel());
            });
        } else {
            this.nmsDialogService.openDialog(
                {
                    description: this.translate.instant("dwdm.circuit.wizard.table.amplifiers.empty.reset.amplifiers"),
                    acceptLabel: "OK",
                    bindHtml: true
                },
                { maxWidth: "800px" }
            );
        }
    }

    private setTableData(configuredAmplifiers: Array<Amplifier>) {
        this.tableData = this.originalAmplifiers.map(amplifier => {
            const configuredAmp = configuredAmplifiers?.find(configured => configured.identifier === amplifier.identifier);

            return configuredAmp ? this.mergeAmplifiers(amplifier, configuredAmp) : amplifier;
        });
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        await this.resolveModel();
        this.circuitDiagram = this.diagramConverterService.convert(cache);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.model,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.endsModel = cache["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = cache["LinksStepModel"] as LinksStepModel;
        this.model = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel() {
        return {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.model,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
    }

    private async openAmplifierModalAndUpdateTable(amplifier: Amplifier) {
        const amplifierData: AmplifierConfigInfo = this.amplifiersUtilsService
            .convertAmplifierToAmplifierConfigInfo(amplifier);

        const amplifierModal: AmplifierConfigInfo = await this.modalFactory
            .openSimpleModal(ConfigAmplifierComponent, amplifierData);

        this.tableData = this.mergeAmplifierConfigInfoToTable(amplifier, amplifierModal);

        if (amplifierModal) {
            this.emitChangesWereMade(this.getCurrentStepModel());
        }
    }

    private mergeAmplifierConfigInfoToTable(amplifier: Amplifier, amplifierConfigInfo: AmplifierConfigInfo) {
        return this.tableData.map(amplifierTable => {
            if (this.isSameAmplifier(amplifierTable, amplifier)) {
                const addedAmplifier = {
                    links: amplifierConfigInfo.links,
                    profile: amplifierConfigInfo.profile,
                    profileLabel: PROFILE_LABEL_MAP[amplifierConfigInfo.profile],
                    mode: amplifierConfigInfo.mode,
                    modeLabel: MODE_LABEL_MAP[amplifierConfigInfo.mode],
                    value: amplifierConfigInfo.value.toFixed(1),
                    limit: amplifierConfigInfo.limit.toFixed(1),
                    losThreshold: amplifierConfigInfo.losThreshold.toFixed(1),
                    losAction: (amplifierConfigInfo.losAction) ? LosAction.ENABLED : LosAction.DISABLED
                };

                amplifierTable = { ...amplifierTable, ...addedAmplifier };
            }

            return amplifierTable;
        });
    }

    private getColumnTextConfig(row: Amplifier): NmsTextLabelIcon {
        const hasLabelIcon = !row.value;
        const tooltipLabelText = hasLabelIcon ? this.translate.instant("dwdm.circuit.wizard.table.amplifier.not.configured") : "";
        const textColor = hasLabelIcon ? PRIMARY_DANGER_COLOR : PRIMARY_PRESSED_COLOR ;

        return {
            iconPosition: "right",
            hasLabelIcon,
            iconType: "material",
            icon: "error",
            tooltipLabelText,
            textColor
        };
    }

    private initializeTableColumns() {
        this.amplifiersTableColumns = [
            {
                field: "identifier",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.identifier"),
                width: "150px",
                optionalColumn: {
                    type:  ColumnConfigType.TEXT_TIPICON,
                    hasTooltip: false,
                    convertTextIcon: (row: any): NmsTextLabelIcon => this.getColumnTextConfig(row),
                    customIconStyle: (row): CssStyle => ({
                        "font-size": "15px",
                        "margin": "0 3px",
                        "color": PRIMARY_DANGER_COLOR,
                        "font-variation-settings": "'FILL' 0",
                        "font-weight": "normal"
                    })
                }
            },
            {
                field: "hostname",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.hostname"),
            },
            {
                field: "portName",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.port"),
                optionalColumn: {
                    type:  ColumnConfigType.TEXT_TIPICON,
                    hasTooltip: true,
                    tooltipContent: (row) => {
                        return this.amplifiersData.find(amplifier =>
                            amplifier.links.find(link => link.linkId === row.id))?.portDescription || row.portName;
                    }
                }
            },
            {
                field: "profileLabel",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.profile"),
                optionalColumn: {
                    type:  ColumnConfigType.TEXT_TIPICON,
                    hasTooltip: false,
                    convertTextIcon: (row): NmsTextLabelIcon => ({
                        iconPosition: "right",
                        hasLabelIcon: true,
                        iconType: "svg",
                        icon: "arrow_right",
                        tooltipLabelText: row["profileLabel"],
                    }),
                    customIconStyle: (row): CssStyle => ({
                        "background-image": row["profile"] === DwdmCircuitAmplifierProfileData.BOOSTER ?
                            "url('/images/booster.svg')" :
                            "url('/images/preamp.svg')",
                        "background-size": "23px 23px",
                        "height": "23px",
                        "margin-left": row["profile"] === DwdmCircuitAmplifierProfileData.BOOSTER ? "11px" : "7px"
                    })
                }
            },
            {
                field: "modeLabel",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.mode"),
            },
            {
                field: "value",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.value"),
            },
            {
                field: "limit",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.limit"),
            },
            {
                field: "losThreshold",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.losThreshold"),
            },
            {
                field: "losAction",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.losAction"),
            },
            {
                field: "actions",
                label: this.translate.instant("dwdm.circuit.wizard.amplifiers.step.actions"),
                width: "10%",
                optionalColumn: {
                    type: ColumnConfigType.ACTIONS,
                    actions: [
                        {
                            id: "edit",
                            iconName: "edit_square",
                            onAction: (row) => this.openAmplifierModalAndUpdateTable(row),
                            getTooltip: () => {
                                return this.translate.instant("buttons.editEntry");
                            }
                        },
                        {
                            id: "remove",
                            iconName: "ink_eraser",
                            onAction: (row) => this.cleanAmplifierFromTable(row),
                            getTooltip: () => {
                                return this.translate.instant("buttons.deleteEntry");
                            }
                        }
                    ]
                },
            }
        ];
    }

    /**
    * Mescla dados do amplificador original com as configurações,
    * preservando o identifier do original para manter consistência.
    */
    private mergeAmplifiers(original: Amplifier, configured: Amplifier): Amplifier {
        return { ...original, ...configured, identifier: original.identifier };
    }

    /**
     * Compara dois amplificadores usando portIdentifier.
     * Isso garante identificação correta mesmo em cenários com dispositivos remotos.
     */
    private isSameAmplifier(amplifier1: Amplifier, amplifier2: Amplifier): boolean {
        const portIdentifier1 = amplifier1.portIdentifier;
        const portIdentifier2 = amplifier2.portIdentifier;

        return this.compareFlatObjectPipe.transform(portIdentifier1, portIdentifier2);
    }

    /**
     * Verifica se houve mudanças na potência dos transponders
     */
    private checkTransponderPowerChanges(): void {
        if (this.powerChangeAlertShown) {
            return;
        }

        if (this.transpondersModel.hasTxPowerChanges) {
            this.powerChangeAlertShown = true;

            this.nmsDialogService.openDialog({
                title: this.translate.instant("dwdm.circuit.wizard.transponders.power.alert.title"),
                description: this.translate.instant("dwdm.circuit.wizard.transponders.power.alert.message"),
                acceptLabel: "OK",
                bindHtml: true
            });
        }
    }
}
